<script setup>
import { computed, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFlexcallsCall } from 'dashboard/composables/useFlexcallsCall';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversation: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

// Gate: only show when the account has the calling module (modulo-ligacoes)
// enabled AND an active agent with a flexcalls_call node configured —
// otherwise the operator clicks and hits an error they cannot fix
// themselves (module state check lives in useFlexcallsCall.js, same
// background-safe pattern as WacallsCallButton/useCallDevices).
const { hasCallCapableAgent, placeCall } = useFlexcallsCall();
const shouldRender = computed(() => hasCallCapableAgent.value);

// Real states: idle -> dialing (disabled) -> success | error. Auto-resets to
// idle after a short delay so the button is usable again without a reload.
const RESET_DELAY_MS = 6000;
const state = ref('idle'); // idle | dialing | success | error
const errorMessage = ref('');
let resetTimer = null;

const scheduleReset = () => {
  clearTimeout(resetTimer);
  resetTimer = setTimeout(() => {
    state.value = 'idle';
    errorMessage.value = '';
  }, RESET_DELAY_MS);
};

onBeforeUnmount(() => clearTimeout(resetTimer));

const buttonIcon = computed(() => {
  if (state.value === 'success') return 'i-ph-check-circle';
  if (state.value === 'error') return 'i-ph-warning-circle';
  return 'i-ph-phone-outgoing';
});

const tooltipLabel = computed(() => {
  if (state.value === 'dialing')
    return t('CONVERSATION.FLEXCALLS_CALL.DIALING');
  if (state.value === 'success')
    return t('CONVERSATION.FLEXCALLS_CALL.SUCCESS');
  if (state.value === 'error') return errorMessage.value;
  return t('CONVERSATION.FLEXCALLS_CALL.CALL');
});

const onClick = async () => {
  if (state.value === 'dialing') return;

  state.value = 'dialing';
  errorMessage.value = '';

  try {
    await placeCall(props.conversation?.id);
    state.value = 'success';
  } catch (e) {
    state.value = 'error';
    // The backend surfaces PlaceCallService's own message verbatim (e.g.
    // insufficient balance, assistant not synced) — show it as-is instead of
    // flattening every failure into a generic "something went wrong".
    errorMessage.value =
      e?.response?.data?.error ||
      t('CONVERSATION.FLEXCALLS_CALL.ERROR_GENERIC');
  } finally {
    scheduleReset();
  }
};
</script>

<template>
  <span class="contents">
    <span class="relative inline-flex">
      <Button
        v-if="shouldRender"
        v-tooltip.top-end="tooltipLabel"
        :icon="buttonIcon"
        :is-loading="state === 'dialing'"
        :disabled="state === 'dialing'"
        sm
        faded
        slate
        @click="onClick"
      />
      <span
        v-if="state === 'error'"
        role="alert"
        class="absolute left-1/2 top-full z-10 mt-1 w-48 -translate-x-1/2 rounded-md bg-n-ruby-9 px-2 py-1 text-center text-xs text-white shadow-lg"
      >
        {{ errorMessage }}
      </span>
      <span
        v-else-if="state === 'success'"
        class="absolute left-1/2 top-full z-10 mt-1 w-40 -translate-x-1/2 rounded-md bg-n-teal-9 px-2 py-1 text-center text-xs text-white shadow-lg"
      >
        {{ t('CONVERSATION.FLEXCALLS_CALL.SUCCESS') }}
      </span>
    </span>
  </span>
</template>
